import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { useMediaQuery } from 'react-responsive';
import { getShortenedString } from '../../../../models/UiUtils';
import { ITemplateCard } from '../../../../viewmodels/AppViewModels';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { baseStyleSheet } from '../../../styles/styles';
import { SimpleTooltip } from '../../Tooltip';
import { s as tooltipStyles } from '../../Tooltip/styles';
import { styleSheet } from './styles';

export const CampaignTemplateCardTruncatedBody = ({
	style,
	showReadMore = true,
	text,
	truncated,
	fullText,
	useTwoLineDisplay = false,
}: {
	style?: StyleDeclarationValue[];
	showReadMore?: boolean;
	text: string;
	truncated: boolean;
	fullText?: string;
	useTwoLineDisplay?: boolean;
}) => {
	const contentElement = (
		<p className={css(styleSheet.textContent, useTwoLineDisplay && styleSheet.textContentTwoLines, ...(style || []))}>
			<span>
				{text}
				{truncated && !useTwoLineDisplay ? '... ' : ''}
			</span>
			{truncated && showReadMore && !useTwoLineDisplay ? (
				<strong className={css(baseStyleSheet.fontBold)} dangerouslySetInnerHTML={{ __html: 'Read more' }} />
			) : null}
		</p>
	);

	// If we have full text and are using two-line display, wrap with tooltip
	if (useTwoLineDisplay && fullText && fullText !== text) {
		return (
			<SimpleTooltip title={fullText} styles={[tooltipStyles.contentWithMaxWidth]}>
				{contentElement}
			</SimpleTooltip>
		);
	}

	return contentElement;
};

export const CampaignTemplateCardMenu = ({
	card,
	onDelete,
	onShare,
	userSession,
}: {
	card: ITemplateCard;
	onDelete?: () => void;
	onShare?: (val: boolean) => void;
	userSession: Api.UserSessionContext;
}) => {
	const canShare =
		onShare && (card.creator?.id === userSession?.user?.id || userSession?.user?.role?.toLowerCase().includes('admin'));

	return (
		<div className={css(styleSheet.menu)}>
			<Dropdown>
				<DropdownEllipsisTrigger />
				<DropdownContent>
					<DropdownItem onClick={() => onDelete?.()}>Delete</DropdownItem>
					{canShare && card?.scope === Api.TemplateScope.Account ? (
						<DropdownItem onClick={() => onShare?.(false)}>Unshare</DropdownItem>
					) : null}
					{canShare && card?.scope !== Api.TemplateScope.Account ? (
						<DropdownItem onClick={() => onShare?.(true)}>Share</DropdownItem>
					) : null}
				</DropdownContent>
			</Dropdown>
		</div>
	);
};

export const CampaignSummaryFromContentShort = ({ card }: { card: ITemplateCard }) => {
	const isLargeScreen = useMediaQuery({ minWidth: 1200 });
	const maxCharCount = isLargeScreen ? 120 : 100;
	const summary = card?.summary;
	const shortSummary = summary ? getShortenedString(summary?.trim(), maxCharCount) : '';
	return (
		<div className={css(styleSheet.textContainer)}>
			<CampaignTemplateCardTruncatedBody
				style={[styleSheet.textContentSmall]}
				text={shortSummary}
				truncated={summary?.length >= maxCharCount}
				showReadMore={false}
			/>
		</div>
	);
};

const htmlCache = new Map();
function getParsedHTML(html: string) {
	if (!htmlCache.has(html)) {
		const dom = new DOMParser().parseFromString(html, 'text/html').body.firstChild;
		htmlCache.set(html, dom);
	}
	return htmlCache.get(html) as HTMLElement;
}

export const CampaignSummaryFromContent = ({ card }: { card: ITemplateCard }) => {
	const parsedHTML = getParsedHTML(card?.content?.document);

	return <div className={css(styleSheet.summary)} dangerouslySetInnerHTML={{ __html: parsedHTML.outerHTML }} />;
};
